import { useEffect, type ReactNode } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { SolanaProvider, AuthProvider, ConfigProvider } from "@/providers";
import { ToastProvider, BottomNav } from "@/components/ui";
import type { NavTab } from "@/components/ui";
import OnboardingPage from "@/pages/OnboardingPage";
import HomePage from "@/pages/HomePage";
import CoinPage from "@/pages/CoinPage";
import CodevCoinPage from "@/pages/CodevCoinPage";
import CabalSuccessPage from "@/pages/CabalSuccessPage";
import MyCabalsPage from "@/pages/MyCabalsPage";
import ActiveCabalPage from "@/pages/ActiveCabalPage";
import JoinCabalPage from "@/pages/JoinCabalPage";
import DepositCabalPage from "@/pages/DepositCabalPage";
import StakeConfirmedPage from "@/pages/StakeConfirmedPage";
import WalletPage from "@/pages/WalletPage";
import NotFoundPage from "@/pages/NotFoundPage";

/* ─── App root with routing ─── */
function App() {
  return (
    <Sentry.ErrorBoundary fallback={<p>Something went wrong.</p>}>
      <BrowserRouter>
        <ScrollToTop />
        <ConfigProvider>
        <SolanaProvider network={(import.meta.env.VITE_SOLANA_NETWORK as "devnet" | "mainnet-beta") || "devnet"}>
          <AuthProvider>
            <ToastProvider>
              <OnboardingGate>
                <AppLayout>
                  <Routes>
                    <Route path="/onboarding" element={<OnboardingPage />} />
                    <Route path="/coins/:coinId" element={<CoinPage />} />
                    <Route path="/create" element={<CodevCoinPage />} />
                    <Route path="/create/success" element={<CabalSuccessPage />} />
                    <Route path="/wallet" element={<WalletPage />} />
                    <Route path="/codevs" element={<MyCabalsPage />} />
                    <Route path="/codevs/join/:inviteCode" element={<JoinCabalPage />} />
                    <Route path="/codevs/:cabalId" element={<ActiveCabalPage />} />
                    <Route path="/codevs/:cabalId/deposit" element={<DepositCabalPage />} />
                    <Route path="/codevs/:cabalId/staked" element={<StakeConfirmedPage />} />
                    <Route path="/" element={<HomePage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </AppLayout>
              </OnboardingGate>
            </ToastProvider>
          </AuthProvider>
        </SolanaProvider>
        </ConfigProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}

/** Scrolls to top on every route change */
function ScrollToTop(): ReactNode {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/** Shared layout that renders BottomNav on all pages except onboarding */
function AppLayout({ children }: { children: ReactNode }): ReactNode {
  const location = useLocation();
  const navigate = useNavigate();
  const showNavBarPages = [
    "/",
    "/wallet",
    "/codevs"
  ]
  const showNavBar = showNavBarPages.includes(location.pathname);

  function getActiveTab(): NavTab {
    const path = location.pathname;
    if (path.startsWith("/codevs")) return "cabals";
    if (path.startsWith("/wallet")) return "wallet";
    if (path.startsWith("/create")) return "create";
    return "home";
  }

  function handleNavChange(tab: NavTab) {
    if (tab === "home") navigate("/");
    else if (tab === "cabals") navigate("/codevs");
    else if (tab === "create") navigate("/create");
    else if (tab === "wallet") navigate("/wallet");
  }

  return (
    <>
      {children}
      {showNavBar && (
        <BottomNav activeTab={getActiveTab()} onTabChange={handleNavChange} />
      )}
    </>
  );
}

/** Redirects to /onboarding if user hasn't completed it yet */
function OnboardingGate({ children }: { children: ReactNode }): ReactNode {
  const location = useLocation();
  const hasOnboarded = localStorage.getItem("codev_onboarded") === "true";

  if (!hasOnboarded && location.pathname !== "/onboarding") {
    const returnTo = location.pathname + location.search + location.hash;
    return <Navigate to={`/onboarding?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  return children;
}

export default App;
